import { ACTIVITY_ID_COOKIE_KEY } from '@eos/b4b-shared-utility/activityId';
import { clientLogger } from '@eos/b4b-shared-utility/clientLogger';
import { postNavigate } from '@eos/b4b-shared-utility/postNavigate';
import { Locale, locales } from '@eos/shared-locale-utility/locale';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { createLogEntry } from './createLogEntry';
import { defaultQueryOptions } from './defaultQueryOptions';

const publicPaths = ['/error', '/session-timeout'];
const fullPublicPaths = publicPaths.flatMap((path) =>
  locales.map((locale) => `/xapp/${locale}${path}`)
);

export interface CreateClientSideQueryClientParams {
  retryStatusCodes?: number[];
  retryCount?: number;
  locale: Locale;
  bookingComUserId?: string;
  redirectIfBcomUserIdMissingFeatureFlagEnabled?: boolean;
}

export const createClientSideQueryClient = ({
  retryStatusCodes = [502, 504],
  retryCount = 1,
  locale,
  bookingComUserId,
  redirectIfBcomUserIdMissingFeatureFlagEnabled,
}: CreateClientSideQueryClientParams) => {
  const handleRetry = (failureCount: number, error: unknown) => {
    const status = (error as AxiosError).response?.status;

    return (
      !!status && retryStatusCodes.includes(status) && failureCount < retryCount
    );
  };

  const handleError = async (error: unknown) => {
    const status = (error as AxiosError).response?.status;
    const message = (error as AxiosError).message;
    const activityId = Cookies.get(ACTIVITY_ID_COOKIE_KEY) ?? '';

    const isAuthenticatedPath = !fullPublicPaths.includes(
      window.location.pathname
    );

    if (status === 400 && isAuthenticatedPath) {
      if (!bookingComUserId && redirectIfBcomUserIdMissingFeatureFlagEnabled) {
        postNavigate('/Web/Account/WebAppLogout');
        return;
      }
    }

    if (status === 401 && isAuthenticatedPath) {
      if (!bookingComUserId && redirectIfBcomUserIdMissingFeatureFlagEnabled) {
        postNavigate('/Web/Account/WebAppLogout');
        return;
      }
      window.location.reload();
    }

    const log = createLogEntry({
      error,
      message,
      getActivityId: () => activityId,
    });

    return await clientLogger.log(log);
  };

  return new QueryClient({
    defaultOptions: {
      queries: {
        ...defaultQueryOptions,
        retry: handleRetry,
      },
    },
    queryCache: new QueryCache({
      onError: handleError,
    }),
    mutationCache: new MutationCache({
      onError: handleError,
    }),
  });
};
